import { pricingApi } from '@/apiClient';
import {
  UtilizationPrice,
  PriceWindowUtilization,
  DailyUtilization,
  TimeWindow,
  UtilizationRange,
} from '@/api';
import { dynPricingDayOfWeek } from '@/utils/dateUtils';

const DEFAULT_START = '1900-01-01T00:00:00';
const DEFAULT_END = '1900-01-01T23:59:59';

export function usePriceWindowPersistence() {
  async function saveUtilizationPrices(
    utilizationPrices: UtilizationPrice[],
    priceWindowId: number | undefined
  ) {
    const priceWindowUtilization = buildPriceWindowUtilization(
      utilizationPrices,
      priceWindowId
    );

    const response = await pricingApi.pricingDetailsPost(
      priceWindowUtilization
    );
    return response;
  }

  function buildPriceWindowUtilization(
    utilizationPrices: UtilizationPrice[],
    priceWindowId: number | undefined
  ): PriceWindowUtilization {
    const dailyUtilizationMap = new Map<number, DailyUtilization>();
    const timeWindowMap = new Map<string, TimeWindow>();

    utilizationPrices.forEach((item) => {
      if (item.dayOfTheWeek == null) return;

      const normalizedDay = dynPricingDayOfWeek(item.dayOfTheWeek);
      const startTime = item.startTime ?? DEFAULT_START;
      const endTime = item.endTime ?? DEFAULT_END;

      ensureDailyUtilizationExists(dailyUtilizationMap, normalizedDay);
      ensureTimeWindowExists(
        timeWindowMap,
        normalizedDay,
        startTime,
        endTime,
        item
      );

      const timeRange = [normalizedDay, startTime, endTime].join('|');
      const timeWindow = timeWindowMap.get(timeRange);

      if (timeWindow) {
        timeWindow.utilizationRanges?.push(buildUtilizationRange(item));
      }
    });

    linkTimeWindowsToDays(timeWindowMap, dailyUtilizationMap);

    return {
      priceWindowId: priceWindowId,
      dailyUtilizations: Array.from(dailyUtilizationMap.values()),
    };
  }

  function ensureDailyUtilizationExists(
    map: Map<number, DailyUtilization>,
    day: number
  ): void {
    if (!map.has(day)) {
      map.set(day, {
        dayOfWeek: day,
        timeWindows: [],
      });
    }
  }

  function ensureTimeWindowExists(
    map: Map<string, TimeWindow>,
    day: number,
    startTime: string,
    endTime: string,
    item: UtilizationPrice
  ): void {
    const timeRange = [day, startTime, endTime].join('|');

    if (!map.has(timeRange)) {
      map.set(timeRange, {
        startTime,
        endTime,
        timeWindowDescription: item.timeWindowDescription,
        utilizationRanges: [],
      });
    }
  }

  function buildUtilizationRange(item: UtilizationPrice): UtilizationRange {
    return {
      low: item.low,
      high: item.high,
      pricePercentage: item.usePercent ? item.pricePercentage : 100,
      priceAmount: item.usePercent ? null : item.priceAmount,
      id: item.id,
      usePercent: item.usePercent,
    };
  }

  function linkTimeWindowsToDays(
    timeWindowMap: Map<string, TimeWindow>,
    dailyUtilizationMap: Map<number, DailyUtilization>
  ): void {
    timeWindowMap.forEach((timeWindow, key) => {
      const day = Number(key.split('|')[0]);
      const dailyUtilization = dailyUtilizationMap.get(day);
      dailyUtilization?.timeWindows?.push(timeWindow);
    });
  }

  return {
    saveUtilizationPrices,
  };
}
